import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@beamx/shared';
import { useApiKey } from '../lib/auth/ApiKeyContext.js';

/**
 * Until Phase 13 self-serve signup, the tenant operator pastes the admin-issued API key.
 * Persisted to localStorage and attached as a bearer token on every request.
 */
export function ApiKeyPromptPage() {
  const { set } = useApiKey();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed.startsWith('beamx_live_') && !trimmed.startsWith('beamx_test_')) {
      setError('Expected a key that starts with beamx_live_ or beamx_test_.');
      return;
    }
    set(trimmed);
    navigate('/', { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-muted/40 to-background px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
            <span className="text-base font-bold">B</span>
          </div>
          <span className="text-lg font-semibold tracking-tight">BeamX</span>
        </div>
        <Card>
          <CardHeader>
            <p className="text-xs font-medium uppercase tracking-wider text-primary">Console</p>
            <CardTitle>Connect your tenant</CardTitle>
            <CardDescription>
              Paste the API key your DigitalWebX admin issued you. It’s stored in your browser
              and sent only as the Authorization header.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="api-key">API key</Label>
                <Input
                  id="api-key"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="beamx_live_…"
                  type="password"
                  required
                  autoFocus
                  aria-invalid={error !== null}
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full">
                Connect
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
